import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { api } from "../lib/api.js";
import { useToast } from "./Toast.js";

/**
 * Bridges the notifications feed into the app-wide toast stack: any newly
 * arrived notification (a share, a team add) pops as a toast as well as
 * landing in the bell. Shares the ["notifications"] query with NotificationBell
 * (React Query dedupes to a single poll). Mounted once, alongside the bell.
 */
export function NotificationToaster() {
  const { push } = useToast();
  // null until the first fetch is seeded — existing notifications must NOT
  // toast on load, only ones that arrive during the session.
  const seen = useRef<Set<string> | null>(null);
  const feed = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.notifications(),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const items = feed.data?.notifications;

  useEffect(() => {
    if (!items) return;
    if (seen.current === null) {
      seen.current = new Set(items.map((n) => n.id));
      return;
    }
    // Oldest-first so a batch stacks newest-at-bottom, matching arrival order.
    for (const n of [...items].reverse()) {
      if (seen.current.has(n.id)) continue;
      seen.current.add(n.id);
      if (n.readAt) continue; // already read elsewhere — no toast
      push(n.body ? `${n.title} — ${n.body}` : n.title, "info");
    }
  }, [items, push]);

  return null;
}

/**
 * Header notification bell. Polls the feed, shows an unread badge, and opens
 * a dropdown of recent items (each links into the app). Opening marks
 * everything read. Edition-agnostic — shares notify everywhere; team adds in
 * b2c.
 */
export function NotificationBell() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const feed = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.notifications(),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const markRead = useMutation({
    mutationFn: () => api.markNotificationsRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const unread = feed.data?.unreadCount ?? 0;
  const items = feed.data?.notifications ?? [];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next && unread > 0) markRead.mutate();
        }}
        className="relative text-white/55 hover:text-white transition-colors duration-150 p-1"
        aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ""}`}
      >
        <Bell size={18} strokeWidth={1.6} aria-hidden className="block" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-[var(--color-mx-magenta,#cc3a9d)] text-white text-[10px] leading-[16px] text-center font-medium">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded border mx-divider bg-black/90 backdrop-blur z-50 shadow-xl">
          {items.length === 0 ? (
            <p className="px-4 py-6 text-center text-white/45 text-sm">
              No notifications
            </p>
          ) : (
            <ul className="divide-y divide-[var(--color-mx-line)]">
              {items.map((n) => {
                const inner = (
                  <div className="px-4 py-3">
                    <p className="text-[13px] text-white/90">{n.title}</p>
                    {n.body && (
                      <p className="text-[12px] text-white/50 mt-0.5">{n.body}</p>
                    )}
                    <p className="mx-mono-meta mt-1 text-[10px] text-white/30">
                      {new Date(n.createdAt).toLocaleString()}
                    </p>
                  </div>
                );
                return (
                  <li key={n.id} className="hover:bg-white/[0.03]">
                    {n.link ? (
                      <a
                        href={n.link}
                        onClick={() => setOpen(false)}
                        className="block"
                      >
                        {inner}
                      </a>
                    ) : (
                      inner
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
